"use client";

import { useState } from "react";
import type { InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes, ElementType, ReactNode } from "react";
import { MapPin, Phone, Mail, Send } from "lucide-react";
import { FaEnvelope, FaHandshake } from 'react-icons/fa';
import clsx from "clsx";
import { API_BASE_URL } from '@/app/lib/api';

// --- Icônes personnalisées (SVG en dur, refactorisées pour utiliser `currentColor`) ---
// La couleur est maintenant contrôlée par la classe `text-*` du parent, ce qui les rend plus réutilisables.

// Inline SVG icons removed — using react-icons (FaEnvelope, FaHandshake) for consistency


// --- Le composant principal ---
export default function HeadContact() {
  const [activeForm, setActiveForm] = useState<"contact" | "revendeur">("contact");

  const gouvernoratOptions = ["Choisir Gouvernorat...", "Tunis", "Ariana", "Manouba", "Ben Arous", "Nabeul", "Bizerte", "Zaghouan", "Sousse", "Monastir", "Mahdia", "Sfax", "Beja", "Jendouba", "El Kef", "Siliana", "Kairouan", "Sidi Bouzid", "Kasserine", "Gabes", "Medenine", "Gafsa", "Tozeur", "Tataouine", "Kebili"];
  const activityOptions = ["Choisir Domaine d’activité...", "E-commerce", "Magasin spécialisé", "Supérette", "Autre"];

  return (
    <div className="bg-gray-50 text-gray-800 font-sans antialiased">
      <div className="container mx-auto px-4 py-16 sm:py-24">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-12">

          {/* Colonne de gauche: Formulaires */}
          <div className="lg:col-span-3">
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg">
              <div className="mb-8 text-center lg:text-left">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Des questions ?</h1>
                <p className="mt-3 text-lg text-gray-600">
                  Nos services sont là pour vous aider. Sélectionnez une option pour que nous puissions vous orienter au mieux.
                </p>
              </div>

              {/* Boutons de sélection de formulaire */}
              <div className="flex justify-center items-stretch gap-4 md:gap-6 mb-10">
                <ToggleButton
                  label="Contact"
                  icon={<FaEnvelope className="w-10 h-10" />}
                  isActive={activeForm === "contact"}
                  onClick={() => setActiveForm("contact")}
                />
                <ToggleButton
                  label="Devenir Revendeur"
                  icon={<FaHandshake className="w-10 h-10" />}
                  isActive={activeForm === "revendeur"}
                  onClick={() => setActiveForm("revendeur")}
                />
              </div>

              {/* Conteneur des formulaires */}
              <div className="min-h-[480px]">
                {activeForm === 'contact' && <ContactForm gouvernoratOptions={gouvernoratOptions} />}
                {activeForm === 'revendeur' && <ResellerForm gouvernoratOptions={gouvernoratOptions} activityOptions={activityOptions} />}
              </div>
            </div>
          </div>

          {/* Colonne de droite: Informations */}
          <div className="lg:col-span-2">
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg h-full">
              <h2 className="text-2xl font-bold mb-6 text-gray-900">Nos Coordonnées</h2>
              <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden mb-6">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3190.150820843477!2d10.229783515579713!3d36.74772287995966!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12fd3641f4084b61%3A0x8d4a619e582d706a!2sGEI%20G%C3%A9n%C3%A9rale%20d'%C3%89quipement%20Industriel!5e0!3m2!1sfr!2stn!4v1672834512345!5m2!1sfr!2stn"
                  className="w-full h-full border-0"
                  allowFullScreen
                  loading="lazy"
                  title="Localisation de notre entreprise"
                />
              </div>
              <div className="space-y-6 text-gray-700">
                <InfoItem icon={MapPin} title="Adresse">
                  <a href="https://maps.app.goo.gl/BuBG1FkSRCDzZAq97" className="hover:text-primary transition-colors" target="_blank" rel="noopener noreferrer">
                    54 rue de Mercure, Ben Arous ZI 2013, Tunisie
                  </a>
                </InfoItem>
                <InfoItem icon={Phone} title="Téléphone">
                  <a href="tel:+21698148481" className="hover:text-primary transition-colors">+216 98 148 481</a>
                </InfoItem>
                <InfoItem icon={Mail} title="Email">
                  <a href="mailto:contact@parex.tn" className="hover:text-primary transition-colors">contact@parex.tn</a>
                </InfoItem>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Composants Helpers pour la UI ---

const ToggleButton = ({ label, icon, isActive, onClick }: { label: string; icon: ReactNode; isActive: boolean; onClick: () => void; }) => (
  <button
    type="button"
    onClick={onClick}
    className={clsx(
      "group flex flex-grow flex-col items-center justify-center p-4 aspect-square rounded-xl transition-all duration-300 transform focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/80",
      {
        'bg-main text-white shadow-xl scale-105': isActive,
        'bg-gray-200 text-gray-500 hover:bg-gray-200 hover:shadow-md hover:text-primary border-2 border-transparent hover:border-primary/20': !isActive,
      }
    )}
  >
    <div className="mb-3 transition-colors duration-300">
      {icon}
    </div>
    <span className={clsx(
      "font-semibold text-sm sm:text-base text-center transition-colors duration-300",
      { 'text-white': isActive, 'text-gray-700 group-hover:text-primary': !isActive }
    )}>
      {label}
    </span>
  </button>
);

type FormInputProps = InputHTMLAttributes<HTMLInputElement> & { label: string };

const FormInput = ({ id, label, required = false, ...props }: FormInputProps) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label} {required && <span className="text-primary">*</span>}</label>
    <input
      id={id}
      required={required}
      className="w-full p-3 bg-gray-50 border-2 border-gray-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors"
      {...props}
    />
  </div>
);

type FormSelectProps = SelectHTMLAttributes<HTMLSelectElement> & { label: string, options: string[] };

const FormSelect = ({ id, label, options, required = false, ...props }: FormSelectProps) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label} {required && <span className="text-primary">*</span>}</label>
    <select
      id={id}
      required={required}
      className="w-full p-3 bg-gray-50 border-2 border-gray-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors"
      {...props}
      value={props.value ?? ""}
    >
      {options.map((opt, i) => (
        <option key={i} value={opt.startsWith("Choisir") ? "" : opt} disabled={opt.startsWith("Choisir")}>
          {opt}
        </option>
      ))}
    </select>
  </div>
);

type FormTextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string };

const FormTextarea = ({ id, label, required = false, ...props }: FormTextareaProps) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label} {required && <span className="text-primary">*</span>}</label>
    <textarea
      id={id}
      rows={4}
      required={required}
      className="w-full p-3 bg-gray-50 border-2 border-gray-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors"
      {...props}
    ></textarea>
  </div>
);

const FormSubmitButton = ({ children, isLoading }: { children: ReactNode, isLoading?: boolean }) => (
  <button
    type="submit"
    disabled={isLoading}
    className="w-full bg-main text-white py-3 px-6 rounded-lg font-semibold hover:bg-opacity-90 transition-all duration-300 text-lg flex items-center justify-center gap-2 group disabled:bg-gray-400 disabled:cursor-not-allowed"
  >
    {isLoading ? 'Envoi en cours...' : children}
    <Send className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
  </button>
);

const ContactForm = ({ gouvernoratOptions }: { gouvernoratOptions: string[] }) => {
  const [formData, setFormData] = useState({
    gouvernorat: "",
    nomContact: "",
    prenomContact: "",
    emailContact: "",
    telephoneContact: "",
    messageRevendeur: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [formStatus, setFormStatus] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setFormStatus(null);

    try {
      const response = await fetch(`${API_BASE_URL}/contact-home`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Une erreur est survenue lors de l\'envoi du message.');
      }

      setFormStatus({ message: 'Message envoyé avec succès !', type: 'success' });
      setFormData({
        gouvernorat: "",
        nomContact: "",
        prenomContact: "",
        emailContact: "",
        telephoneContact: "",
        messageRevendeur: "",
      });
    } catch (error: unknown) {
      const err = error as { message?: string };
      setFormStatus({ message: err?.message ?? 'Une erreur est survenue.', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form className="space-y-5 animate-fade-in" onSubmit={handleSubmit}>
      <h2 className="text-2xl font-bold text-gray-800">Formulaire de Contact</h2>
      <FormSelect id="contact-gouvernorat" name="gouvernorat" label="Gouvernorat" options={gouvernoratOptions} required value={formData.gouvernorat} onChange={handleChange} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FormInput id="contact-nom" name="nomContact" label="Nom" placeholder="Votre nom" required autoComplete="family-name" value={formData.nomContact} onChange={handleChange} />
        <FormInput id="contact-prenom" name="prenomContact" label="Prénom" placeholder="Votre prénom" required autoComplete="given-name" value={formData.prenomContact} onChange={handleChange} />
      </div>
      <FormInput id="contact-email" name="emailContact" label="Email" type="email" placeholder="votre.email@exemple.com" required autoComplete="email" value={formData.emailContact} onChange={handleChange} />
      <FormInput id="contact-tel" name="telephoneContact" label="Téléphone" type="tel" placeholder="Votre numéro" required autoComplete="tel" value={formData.telephoneContact} onChange={handleChange} />
      <FormTextarea id="contact-message" name="messageRevendeur" label="Votre message" placeholder="Posez votre question ici..." required value={formData.messageRevendeur} onChange={handleChange} />
      {formStatus && (
        <div className={`p-3 rounded-lg text-center ${formStatus.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {formStatus.message}
        </div>
      )}
      <FormSubmitButton isLoading={isLoading}>
        Envoyer le Message
      </FormSubmitButton>
    </form>
  );
};

const ResellerForm = ({ gouvernoratOptions, activityOptions }: { gouvernoratOptions: string[], activityOptions: string[] }) => {
  const [formData, setFormData] = useState({
    gouvernorat: "",
    nomContact: "",
    prenomContact: "",
    societe: "",
    activite: "",
    emailContact: "",
    telephoneContact: "",
    messageRevendeur: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [formStatus, setFormStatus] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setFormStatus(null);

    try {
      const response = await fetch(`${API_BASE_URL}/devenir-revendeur`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Une erreur est survenue lors de l\'envoi de la demande.');
      }

      setFormStatus({ message: 'Demande envoyée avec succès !', type: 'success' });
      setFormData({
        gouvernorat: "",
        nomContact: "",
        prenomContact: "",
        societe: "",
        activite: "",
        emailContact: "",
        telephoneContact: "",
        messageRevendeur: "",
      });
    } catch (error: unknown) {
      const err = error as { message?: string };
      setFormStatus({ message: err?.message ?? 'Une erreur est survenue.', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form className="space-y-5 animate-fade-in" onSubmit={handleSubmit}>
      <h2 className="text-2xl font-bold text-gray-800">Demande pour Devenir Revendeur</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FormInput id="reseller-nom" name="nomContact" label="Nom du contact" placeholder="Votre nom" required autoComplete="family-name" value={formData.nomContact} onChange={handleChange} />
        <FormInput id="reseller-prenom" name="prenomContact" label="Prénom du contact" placeholder="Votre prénom" required autoComplete="given-name" value={formData.prenomContact} onChange={handleChange} />
      </div>
      <FormInput id="reseller-societe" name="societe" label="Nom de votre Société" placeholder="Nom de l'entreprise" required autoComplete="organization" value={formData.societe} onChange={handleChange} />
      <FormSelect id="reseller-gouvernorat" name="gouvernorat" label="Gouvernorat" options={gouvernoratOptions} required value={formData.gouvernorat} onChange={handleChange} />
      <FormSelect id="reseller-activite" name="activite" label="Domaine d'activité" options={activityOptions} required value={formData.activite} onChange={handleChange} />
      <FormInput id="reseller-email" name="emailContact" label="Email de contact" type="email" placeholder="email.pro@exemple.com" required autoComplete="email" value={formData.emailContact} onChange={handleChange} />
      <FormInput id="reseller-tel" name="telephoneContact" label="Téléphone de contact" type="tel" placeholder="Numéro professionnel" required autoComplete="tel" value={formData.telephoneContact} onChange={handleChange} />
      <FormTextarea id="reseller-message" name="messageRevendeur" label="Votre message" placeholder="Décrivez brièvement votre activité..." required value={formData.messageRevendeur} onChange={handleChange} />
      {formStatus && (
        <div className={`p-3 rounded-lg text-center ${formStatus.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {formStatus.message}
        </div>
      )}
      <FormSubmitButton isLoading={isLoading}>
        Envoyer la Demande
      </FormSubmitButton>
    </form>
  );
};

const InfoItem = ({ icon: Icon, title, children }: { icon: ElementType, title: string, children: ReactNode }) => (
  <div className="flex items-start">
    <div className="mr-4 mt-1 text-primary flex-shrink-0 bg-primary/10 p-2 rounded-full">
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <h3 className="font-bold text-gray-800 text-lg">{title}</h3>
      <div className="text-gray-600">{children}</div>
    </div>
  </div>
);